/**
 * Session for the rebalance questionnaire routes.
 *
 * Belongs here: mounting the questionnaire session, sticky entry `source`.
 * Does not belong here: cursor rules (see useRebalanceQuestionnaireFlow) or page markup.
 * `riskProfile` is not stored here. The profile page reads it from the query param.
 */
import { createContext, useContext, useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useRebalanceQuestionnaireFlow } from './flow/useRebalanceQuestionnaireFlow'
import {
  parseRebalanceQuestionnaireSource,
  type RebalanceQuestionnaireSource
} from './rebalanceQuestionnairePaths'

type RebalanceQuestionnaireContextValue = ReturnType<typeof useRebalanceQuestionnaireFlow>

const RebalanceQuestionnaireContext = createContext<RebalanceQuestionnaireContextValue | undefined>(
  undefined
)

/** Keeps answers, cursor, sticky `source`, and submit `viewPlanCta` alive across these routes. */
export function RebalanceQuestionnaireProvider() {
  const { search } = useLocation()
  const [entrySource, setEntrySource] = useState<RebalanceQuestionnaireSource | null>(() =>
    parseRebalanceQuestionnaireSource(new URLSearchParams(search).get('source'))
  )

  useEffect(() => {
    const parsed = parseRebalanceQuestionnaireSource(new URLSearchParams(search).get('source'))
    // In-feature routes drop query — only update when URL still carries source
    if (parsed != null) {
      setEntrySource(parsed)
    }
  }, [search])

  const session = useRebalanceQuestionnaireFlow(entrySource)

  return (
    <RebalanceQuestionnaireContext.Provider value={session}>
      <Outlet />
    </RebalanceQuestionnaireContext.Provider>
  )
}

/** Questionnaire session. Must render under RebalanceQuestionnaireProvider. */
export function useRebalanceQuestionnaireContext(): RebalanceQuestionnaireContextValue {
  const value = useContext(RebalanceQuestionnaireContext)
  if (value == null) {
    throw new Error('useRebalanceQuestionnaireContext must be used within RebalanceQuestionnaireProvider')
  }
  return value
}
